/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { FaArrowUpRightFromSquare } from 'react-icons/fa6';
import { FiEdit2 } from 'react-icons/fi';
import { MdOutlineExpandLess, MdOutlineExpandMore } from 'react-icons/md';
import { Button } from 'reactstrap';
import { FSBTable } from 'components/common/FSBTable';
import DeleteConfirmationAlert from 'components/DeleteConfirmationAlert';
import {
  DEMANDS_QUERY,
  DELETE_DEMAND,
  UPDATE_DEMAND_STATUS,
} from 'pages/MarketListing/MarketListingGraphQL';
import { setBidDetails } from 'actions/bidDetailAction';
import {
  MARKET_LIST_STATUS,
  demandsColumns,
  GradeBadge,
} from 'pages/MarketListing/constant';
import StatusFormatter from 'components/StatusFormatter';
import {
  RECORD_DELETED_SUCCESSFULLY,
  STATUS_UPDATED_SUCCESSFULLY,
} from 'components/common/FSBToastify/types';
import Loader from 'components/common/Loader';
import {
  defaultDeleteFilter,
  showSequenceNumber,
  currencyToLocal,
  NumberWithCommasFormat,
} from 'utils/constants';
import DownloadCSV from 'components/DownloadCSV';
import { TYPE_OF_OFFER_AND_DEMAND } from 'helper/constants';
import {
  EXPORTS_DEMANDS,
} from 'utils/CommonGraphQL';
import { setVendorDetails } from 'actions/vendorDetailsAction';
import { role } from 'config/role';
import RBAC from 'hoc/RBAC';
import NoDataFound from 'components/NoDataFound';
import ErrorMessage from 'components/ErrorMessage';
import FilterResultBar from 'components/FilterResultBar';
import MarketListingMobileView from 'pages/MarketListing/components/MarketListingMobileView';
import ReactivateListingModal from 'pages/MarketListing/components/ReactivateListingModal';

const renderRoleColumn = ({ row }) => (
  <div className="px-1 text-capitalize">
    {row?.original?.createdByUser?.traderType?.toLowerCase() || row?.original?.trader?.traderType?.toLowerCase() || '-'}
  </div>
);

const getValidLinkedDemands = (demand) => {
  if (!demand?.linkedDemands || !Array.isArray(demand.linkedDemands)) {
    return [];
  }
  return demand.linkedDemands.filter(
    (item) => String(item?.id) !== String(demand?.id),
  );
};

const renderChildUserColumn = ({ row }) => {
  const demand = row?.original;
  const trader = demand?.trader;
  const createdByUser = demand?.createdByUser;

  // If createdByUser is different from trader, show createdByUser as child user
  if (createdByUser && trader && String(createdByUser.id) !== String(trader.id)) {
    const name = `${createdByUser.firstName || ''} ${createdByUser.lastName || ''}`.trim();
    const callingCode = createdByUser.callingCode?.callingCode || '';
    const phone = createdByUser.phone || '';
    const mobileNumber = phone ? `${callingCode}-${phone}` : '';
    return (
      <div>
        <div className="fw-medium">{name || '-'}</div>
        {mobileNumber && <div className="text-muted f-12">{mobileNumber}</div>}
      </div>
    );
  }

  return '-';
};

const LiveDemandsTable = ({
  searchString,
  filterValue,
  handleResetFilter,
  showAdvancedSearchBar,
  selectedProductName,
}) => {
  const dispatch = useDispatch();
  const [deleteRowId, setDeleteRowId] = useState('');
  const [editRow, setEditRow] = useState(null);
  const [showDeleteRecord, setDeleteRecord] = useState(false);
  const [filters, setFilters] = useState({
    filter: [
      {
        key: 'entityName',
        joinAttributeName: 'trader',
        value: searchString,
        type: 'STRING',
        op: 'LIKE_OR_NULL',
      },
      {
        key: 'status',
        value: 'ACTIVE',
        type: 'STRING',
        op: 'EQ',
      },
      {
        key: 'demandType',
        value: TYPE_OF_OFFER_AND_DEMAND.LIVE,
        type: 'STRING',
        op: 'EQ',
      },
      {
        key: 'totalQty',
        value: 0,
        type: 'INT',
        op: 'GT',
      },
      {
        ...defaultDeleteFilter,
      },
    ],
    pageNo: 1,
    pageSize: 10,
    sortCol: 'rate',
    sortDirection: 'asc',
  });

  // Build the product name filter for GraphQL
  const productNameFilter = useMemo(() => {
    if (!selectedProductName) return [];
    return [
      {
        key: 'name',
        value: selectedProductName,
        joinAttributeName: 'grades.subCategory.product',
        type: 'STRING',
        op: 'EQ',
      },
    ];
  }, [selectedProductName]);

  // Combine all filters for the GraphQL query
  const graphqlVariables = useMemo(() => ({
    filter: [
      ...filters.filter,
      ...filterValue,
      ...productNameFilter,
    ],
    pageNo: filters.pageNo,
    pageSize: filters.pageSize,
    sortCol: filters.sortCol,
    sortDirection: filters.sortDirection,
  }), [filters, filterValue, productNameFilter]);

  const { data, loading, error, refetch } = useQuery(DEMANDS_QUERY, {
    variables: graphqlVariables,
    fetchPolicy: 'network-only',
  });

  const [deleteDemand] = useMutation(DELETE_DEMAND, {
    onCompleted: () => {
      toast.success(RECORD_DELETED_SUCCESSFULLY);
      setDeleteRowId('');
      setDeleteRecord(!showDeleteRecord);
      refetch();
    },

    onError: ({ graphQLErrors }) => {
      graphQLErrors.map(({ message }) => {
        return message;
      });
    },
  });

  const [updateDemandStatus] = useMutation(UPDATE_DEMAND_STATUS, {
    onCompleted: () => {
      toast.success(STATUS_UPDATED_SUCCESSFULLY);
      refetch();
    },
    onError: () => { },
  });

  const toggleDeleteRecord = (id) => {
    setDeleteRecord(!showDeleteRecord);
    setDeleteRowId(id);
  };

  const openEditModal = (row) => {
    setEditRow(row);
  };

  const closeEditModal = () => {
    setEditRow(null);
  };

  const onDeleteConfirmation = async () => {
    try {
      await deleteDemand({
        variables: { id: deleteRowId },
      });
    } catch (err) {
      toast.error(err);
    }
  };

  const handleOfferStatus = async (e, cell) => {
    try {
      await updateDemandStatus({
        variables: { id: cell?.row?.original?.id, status: e.target.value },
      });
    } catch (err) {
      toast.error(err);
    }
  };

  const statusFormatter = (cell) => {
    return (
      <RBAC
        allowedRoles={[role.SUPER_ADMIN, role.ADMIN, role.SALES_HEAD]}
        disabled
      >
        {({ access }) => (
          <StatusFormatter
            options={MARKET_LIST_STATUS}
            defaultValue={cell?.row?.original?.status}
            handleChange={(e) => handleOfferStatus(e, cell)}
            inputDisabled={!access}
          />
        )}
      </RBAC>
    );
  };

  const handleLinkClick = (row) => {
    dispatch(
      setBidDetails({
        status: true,
        selectedBid: {
          ...row,
          bidType: 'demand',
          bidDemandType: 'LIVE',
          filters,
        },
      }),
    );
  };

  const linkFormatter = (cell) => {
    const parentDemand = cell?.row?.original;
    const isExpanded = cell?.row?.getIsExpanded?.();
    const linkedDemands = getValidLinkedDemands(parentDemand);

    return (
      <div className="d-flex flex-column">
        <div
          className="text-blue-c5 fw-medium cursor-pointer d-flex align-items-center"
          style={{ minHeight: '44px' }}
          onClick={() => handleLinkClick(parentDemand)}
          aria-hidden
        >
          <span className="me-2 f-16">{parentDemand?.id}</span>{' '}
          <FaArrowUpRightFromSquare />
        </div>
        {isExpanded &&
          linkedDemands.map((linkedItem) => {
            const mergedItem = {
              ...parentDemand,
              ...linkedItem,
              id: linkedItem.id,
              totalQty: linkedItem.totalQty,
              rate: linkedItem.rate ?? parentDemand.rate,
              grades: linkedItem?.grades?.length ? linkedItem.grades : parentDemand.grades,
            };
            return (
              <div
                key={linkedItem.id}
                className="text-blue-c5 fw-medium cursor-pointer d-flex align-items-center"
                style={{
                  minHeight: '44px',
                  borderTop: '1px dashed #e2e8f0',
                }}
                onClick={() => handleLinkClick(mergedItem)}
                aria-hidden
              >
                <span className="me-2 f-16">{linkedItem.id}</span>{' '}
                <FaArrowUpRightFromSquare />
              </div>
            );
          })}
      </div>
    );
  };

  const gradeFormatter = (cell) => {
    const parentDemand = cell?.row?.original;
    const isExpanded = cell?.row?.getIsExpanded?.();
    const linkedDemands = getValidLinkedDemands(parentDemand);

    return (
      <div className="d-flex flex-column">
        <div
          className="d-flex flex-column align-items-start justify-content-center"
          style={{ minHeight: '44px' }}
        >
          {parentDemand?.grades?.map((grade) => (
            <GradeBadge key={grade?.id} grade={grade} />
          ))}
        </div>
        {isExpanded &&
          linkedDemands.map((linkedItem) => (
            <div
              key={linkedItem.id}
              className="d-flex flex-column align-items-start justify-content-center"
              style={{
                minHeight: '44px',
                borderTop: '1px dashed #e2e8f0',
              }}
            >
              {(linkedItem?.grades?.length ? linkedItem.grades : parentDemand?.grades)?.map((grade) => (
                <GradeBadge key={grade?.id} grade={grade} />
              ))}
            </div>
          ))}
      </div>
    );
  };

  const totalQtyFormatter = (cell) => {
    const parentDemand = cell?.row?.original;
    const isExpanded = cell?.row?.getIsExpanded?.();
    const linkedDemands = getValidLinkedDemands(parentDemand);

    return (
      <div className="d-flex flex-column">
        <div
          className="d-flex align-items-center"
          style={{ minHeight: '44px' }}
        >
          {NumberWithCommasFormat(parentDemand?.totalQty)}{' '}
          {parentDemand?.actualQuantityUnit || parentDemand?.quantityUnit}
        </div>
        {isExpanded &&
          linkedDemands.map((linkedItem) => (
            <div
              key={linkedItem.id}
              className="d-flex align-items-center"
              style={{
                minHeight: '44px',
                borderTop: '1px dashed #e2e8f0',
              }}
            >
              {NumberWithCommasFormat(linkedItem?.totalQty)}{' '}
              {parentDemand?.actualQuantityUnit || parentDemand?.quantityUnit}
            </div>
          ))}
      </div>
    );
  };

  const rateFormatter = (cell) => {
    const parentDemand = cell?.row?.original;
    const isExpanded = cell?.row?.getIsExpanded?.();
    const linkedDemands = getValidLinkedDemands(parentDemand);

    return (
      <div className="d-flex flex-column">
        <div
          className="text-nowrap d-flex align-items-center"
          style={{ minHeight: '44px' }}
        >
          {`${currencyToLocal(
            parentDemand?.rate,
            parentDemand?.rateUnit?.isoCurrencyCode,
          )}/${parentDemand?.quantityUnit}`}
        </div>
        {isExpanded &&
          linkedDemands.map((linkedItem) => (
            <div
              key={linkedItem.id}
              className="text-nowrap d-flex align-items-center"
              style={{
                minHeight: '44px',
                borderTop: '1px dashed #e2e8f0',
              }}
            >
              {`${currencyToLocal(
                linkedItem?.rate ?? parentDemand?.rate,
                parentDemand?.rateUnit?.isoCurrencyCode,
              )}/${parentDemand?.quantityUnit}`}
            </div>
          ))}
      </div>
    );
  };

  const renderEditButton = (row) => {
    return (
      <button
        type="button"
        className="btn btn-outline-primary btn-sm d-inline-flex align-items-center justify-content-center"
        onClick={() => openEditModal(row)}
        title="Edit demand"
        aria-label="Edit demand"
      >
        <FiEdit2 />
      </button>
    );
  };

  const editFormatter = (cell) => (
    <div
      className="d-flex align-items-center"
      style={{ minHeight: '44px' }}
    >
      {renderEditButton(cell?.row?.original)}
    </div>
  );

  const handleNameClick = (row) => {
    if (!row?.trader?.id) return;
    dispatch(
      setVendorDetails({
        status: true,
        selectedVendor: row.trader,
      }),
    );
  };

  const liveDemandsColumns = demandsColumns.map((column) => ({ ...column }));

  const findIndexOfItem = (item) => {
    return liveDemandsColumns.findIndex((itm) => itm.accessorKey === item);
  };

  liveDemandsColumns[findIndexOfItem('status')].cell = statusFormatter;
  liveDemandsColumns[findIndexOfItem('id')].cell = linkFormatter;

  const gradesIdx = findIndexOfItem('grades');
  if (gradesIdx !== -1) {
    liveDemandsColumns[gradesIdx].cell = gradeFormatter;
    liveDemandsColumns[gradesIdx].accessorFn = undefined;
  }

  const totalQtyIdx = findIndexOfItem('totalQty');
  if (totalQtyIdx !== -1) {
    liveDemandsColumns[totalQtyIdx].cell = totalQtyFormatter;
  }

  const rateIdx = liveDemandsColumns.findIndex(
    (itm) => itm.accessorKey === 'rate' || itm.id === 'rate',
  );
  if (rateIdx !== -1) {
    liveDemandsColumns[rateIdx].cell = rateFormatter;
  }

  liveDemandsColumns[findIndexOfItem('SNo')].cell = ({ cell, row }) => {
    return (
      <div
        className="d-flex align-items-center"
        style={{ minHeight: '44px' }}
      >
        <span className="me-1">{showSequenceNumber(cell, filters)}</span>
        {row.getCanExpand() ? (
          <Button
            color="link"
            className="text-decoration-none p-0 text-active"
            type="button"
            {...{
              onClick: row.getToggleExpandedHandler(),
              style: { cursor: 'pointer', lineHeight: 1 },
            }}
          >
            {row.getIsExpanded() ? (
              <MdOutlineExpandLess size={20} />
            ) : (
              <MdOutlineExpandMore size={20} />
            )}
          </Button>
        ) : null}
      </div>
    );
  };
  liveDemandsColumns.splice(findIndexOfItem('SNo'), 0, {
    accessorKey: 'edit',
    header: 'Edit',
    enableSorting: false,
    cell: editFormatter,
  });

  // Update the child user column to use GraphQL data
  const childUserIdx = liveDemandsColumns.findIndex((itm) => itm.accessorKey === 'createdBy');
  if (childUserIdx !== -1) {
    liveDemandsColumns[childUserIdx].cell = renderChildUserColumn;
    liveDemandsColumns[childUserIdx].accessorFn = undefined;
  }

  liveDemandsColumns.push({
    accessorKey: 'role',
    header: 'Role',
    enableSorting: false,
    cell: renderRoleColumn,
  });

  const handlePageChange = ({ pageSize, pageNo }) => {
    setFilters({
      ...filters,
      pageNo: data?.demands?.totalCount < pageSize ? 1 : pageNo,
      pageSize,
    });
  };
  const onSortChange = (sort) => {
    setFilters({
      ...filters,
      sortCol: sort()[0].id,
      sortDirection: sort()[0].desc ? 'DESC' : 'ASC',
    });
  };

  const sortBy = [
    filters.sortCol === null
      ? { id: null, desc: null }
      : {
        id: filters.sortCol,
        desc: filters.sortDirection === 'DESC',
      },
  ];

  useEffect(() => {
    const updatedFilter = [...filters.filter];
    const updatedFirstFilter = { ...updatedFilter[0] };
    updatedFirstFilter.value = searchString;
    updatedFilter[0] = updatedFirstFilter;
    setFilters(prev => ({ ...prev, filter: updatedFilter }));
  }, [searchString]);

  const onGlobalSearch = (str) => {
    if (str) {
      setFilters((prevFilter) => {
        const updatedFilter = [...prevFilter.filter];
        const updatedFirstFilter = { ...updatedFilter[0] };
        updatedFirstFilter.value = str;
        updatedFilter[0] = updatedFirstFilter;
        return { ...prevFilter, filter: updatedFilter };
      });
    }
  };


  if (loading) return <Loader />;
  if (error) {
    return <ErrorMessage error={error} />;
  }
  if (!data?.demands?.demands)
    return <NoDataFound message="Demands not available" />;

  return (
    <>
      {showAdvancedSearchBar && data?.demands?.demands?.length > 0 && (
        <FilterResultBar
          length={data?.demands?.demands?.length}
          type="live demands"
          handleResetFilter={handleResetFilter}
        />
      )}
      <DeleteConfirmationAlert
        open={showDeleteRecord}
        title="Delete"
        message="Do you really want to remove this record?"
        cancelButtonLabel="Close"
        addButtonLabel="Delete"
        onConfirm={onDeleteConfirmation}
        onCancel={toggleDeleteRecord}
      />
      <ReactivateListingModal
        isOpen={!!editRow}
        kind="demand"
        mode="edit"
        row={editRow}
        onClose={closeEditModal}
        onSuccess={refetch}
      />
      <DownloadCSV
        lazyQuery={EXPORTS_DEMANDS}
        filters={{
          filter: graphqlVariables.filter,
          sortCol: filters.sortCol,
          sortDirection: filters.sortDirection,
        }}
        apiKey="exportDemands"
        fileName="LiveDemands"
        formats={['csv', 'excel']}
        classWrapper="market-listing-download"
        positionTop="-34px"
        positionRight="0"
      />
      <MarketListingMobileView
        rows={(data && data?.demands?.demands) || []}
        filters={filters}
        kind="demand"
        totalPages={(data && data?.demands?.totalPages) || 0}
        currentPageNo={filters.pageNo}
        currentPageSize={filters.pageSize}
        handlePageChange={handlePageChange}
        onIdClick={handleLinkClick}
        onNameClick={handleNameClick}
        renderStatusControl={(row, index) =>
          statusFormatter({ row: { original: row, index } })
        }
        renderRowAction={renderEditButton}
      >
        <FSBTable
          data={(data && data?.demands?.demands) || []}
          totalCount={(data && data?.demands?.totalCount) || 0}
          totalPages={(data && data?.demands?.totalPages) || 0}
          columns={liveDemandsColumns}
          currentPageNo={filters.pageNo}
          pageSize={filters.pageSize}
          keyField="id"
          showSearch={false}
          showFilters
          showDelete={false}
          showPagination
          remote
          striped={false}
          hover={false}
          bordered={false}
          wrapperClass="table-responsive word-break"
          handlePageChange={handlePageChange}
          globalSearchStr={filters.filter[0].value}
          onGlobalSearch={onGlobalSearch}
          onSortChange={onSortChange}
          sorting={sortBy}
          searchPlaceholder="Search Vendors"
          getRowCanExpand={(row) =>
            getValidLinkedDemands(row?.original).length > 0
          }
          isRowExpanded={false}
        />
      </MarketListingMobileView>
    </>
  );
};
export default LiveDemandsTable;
